import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, ShieldAlert } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

import { RevenueTab } from '../components/admin/RevenueTab';
import { StorageTab } from '../components/admin/StorageTab';
import { AuditTab } from '../components/admin/AuditTab';
import { AdminSupportTab } from '../components/admin/SupportTab';
import { AddonsRevenueTab } from '../components/admin/AddonsRevenueTab';
import { AmbassadorsTab } from '../components/admin/AmbassadorsTab';
import { HealthTab } from '../components/admin/HealthTab';
import { LlmHealthTab } from '../components/admin/LlmHealthTab';
import StorageHealthTab from '../components/admin/StorageHealthTab';
import { UsersTab } from '../components/admin/UsersTab';
import { RevenueFilters, AuditFilters } from '../components/admin/FilterPanels';
import { AdminHeader, AdminTabs } from '../components/admin/AdminLayout';
import ContentTab from '../components/admin/ContentTab';
import PioneersTab from '../components/admin/PioneersTab';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('health');

  // Revenue data
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [subsLoading, setSubsLoading] = useState(false);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [paymentsData, setPaymentsData] = useState({ summary: null, rows: [] });
  const [subsData, setSubsData] = useState({ summary: null, rows: [] });
  const [eventsData, setEventsData] = useState({ rows: [] });

  // Storage data
  const [storageLoading, setStorageLoading] = useState(false);
  const [storageData, setStorageData] = useState(null);

  // Audit log data
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditRows, setAuditRows] = useState([]);
  const [auditAction, setAuditAction] = useState('');
  const [auditEmail, setAuditEmail] = useState('');

  // Support tickets data
  const [supportTickets, setSupportTickets] = useState([]);
  const [supportLoading, setSupportLoading] = useState(false);
  const [supportStats, setSupportStats] = useState(null);
  const [supportAnalytics, setSupportAnalytics] = useState(null);

  // Add-ons Revenue data
  const [revenueData, setRevenueData] = useState(null);
  const [revenueLoading, setRevenueLoading] = useState(false);

  // Revenue filters
  const [revStart, setRevStart] = useState('');
  const [revEnd, setRevEnd] = useState('');
  const [revStatus, setRevStatus] = useState('all');
  const [revCurrency, setRevCurrency] = useState('all');
  const [revEmail, setRevEmail] = useState('');
  const [revPlan, setRevPlan] = useState('all');

  // Subscription filters (referenced by loadSubscriptions)
  const [subStatus] = useState('all');
  const [subEmail] = useState('');
  const [subPlan] = useState('all');

  // Webhook event filters
  const [evtType] = useState('all');
  const [evtEmail] = useState('');
  const [evtRef] = useState('');

  const loadAmbassadors = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API}/admin/ambassadors`);
      setRows(res.data.rows || []);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const loadPayments = async () => {
    setPaymentsLoading(true);
    try {
      const params = {};
      if (revStart) params.start = new Date(revStart + 'T00:00:00Z').toISOString();
      if (revEnd) params.end = new Date(revEnd + 'T23:59:59Z').toISOString();
      if (revStatus !== 'all') params.status = revStatus;
      if (revCurrency !== 'all') params.currency = revCurrency;
      if (revEmail) params.email = revEmail;
      if (revPlan !== 'all') params.plan_id = revPlan;

      const res = await axios.get(`${API}/admin/payments`, { params });
      setPaymentsData({ summary: res.data.summary, rows: res.data.rows || [] });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load payments');
    } finally {
      setPaymentsLoading(false);
    }
  };

  const loadSubscriptions = async () => {
    setSubsLoading(true);
    try {
      const params = {};
      if (subStatus !== 'all') params.status = subStatus;
      if (subEmail) params.email = subEmail;
      if (subPlan !== 'all') params.plan_id = subPlan;

      const res = await axios.get(`${API}/admin/subscriptions`, { params });
      setSubsData({ summary: res.data.summary, rows: res.data.rows || [] });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load subscriptions');
    } finally {
      setSubsLoading(false);
    }
  };

  const loadEvents = async () => {
    setEventsLoading(true);
    try {
      const params = {};
      if (evtType !== 'all') params.event = evtType;
      if (evtEmail) params.email = evtEmail;
      if (evtRef) params.reference = evtRef;

      const res = await axios.get(`${API}/admin/paystack-events`, { params });
      setEventsData({ rows: res.data.rows || [] });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load webhook events');
    } finally {
      setEventsLoading(false);
    }
  };

  const loadStorage = async () => {
    setStorageLoading(true);
    try {
      const res = await axios.get(`${API}/uploads/admin/storage-overview`);
      setStorageData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load storage data');
    } finally {
      setStorageLoading(false);
    }
  };

  const loadAudit = async () => {
    setAuditLoading(true);
    try {
      const params = {};
      if (auditAction) params.action = auditAction;
      if (auditEmail) params.email = auditEmail;
      const res = await axios.get(`${API}/admin/audit-log`, { params });
      setAuditRows(res.data.rows || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load audit log');
    } finally {
      setAuditLoading(false);
    }
  };

  const loadSupportTickets = async () => {
    setSupportLoading(true);
    try {
      const [ticketsRes, statsRes, analyticsRes] = await Promise.all([
        axios.get(`${API}/support/admin/tickets`),
        axios.get(`${API}/support/admin/stats`),
        axios.get(`${API}/support/admin/analytics?days=30`).catch(() => ({ data: null })),
      ]);
      setSupportTickets(ticketsRes.data.tickets || []);
      setSupportStats(statsRes.data);
      setSupportAnalytics(analyticsRes.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load support tickets');
    } finally {
      setSupportLoading(false);
    }
  };

  const loadAddonsRevenue = async () => {
    setRevenueLoading(true);
    try {
      const res = await axios.get(`${API}/premium/admin/revenue`);
      setRevenueData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load revenue data');
    } finally {
      setRevenueLoading(false);
    }
  };

  useEffect(() => {
    loadAmbassadors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (tab === 'revenue') {
      loadPayments();
      loadSubscriptions();
      loadEvents();
    } else if (tab === 'storage') {
      loadStorage();
    } else if (tab === 'audit') {
      loadAudit();
    } else if (tab === 'support') {
      loadSupportTickets();
    } else if (tab === 'addons_revenue') {
      loadAddonsRevenue();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const downloadBlob = async (url, filename) => {
    const res = await axios.get(url, { responseType: 'blob' });
    const blob = new Blob([res.data], { type: 'text/csv' });
    const objUrl = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(objUrl);
  };

  const handleDownload = async (kind) => {
    const map = {
      ambassadors: [`${API}/admin/ambassadors.csv`, 'ambassadors.csv', 'CSV downloaded'],
      payments: [`${API}/admin/payments.csv`, 'payments.csv', 'Payments CSV downloaded'],
      subscriptions: [`${API}/admin/subscriptions.csv`, 'subscriptions.csv', 'Subscriptions CSV downloaded'],
      events: [`${API}/admin/paystack-events.csv`, 'paystack-events.csv', 'Webhook events CSV downloaded'],
    };
    const target = map[kind];
    if (!target) return;
    try {
      await downloadBlob(target[0], target[1]);
      toast.success(target[2]);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Download failed');
    }
  };

  const handleRefresh = () => {
    if (tab === 'health') return; // HealthTab manages its own refresh via range selector
    if (tab === 'ambassadors') loadAmbassadors();
    else if (tab === 'revenue') { loadPayments(); loadSubscriptions(); loadEvents(); }
    else if (tab === 'storage') loadStorage();
    else if (tab === 'support') loadSupportTickets();
    else if (tab === 'addons_revenue') loadAddonsRevenue();
    else loadAudit();
  };

  const applyRevenueFilters = () => {
    loadPayments();
    loadSubscriptions();
    loadEvents();
  };

  if (loading) {
    return (
      <div className="f500-page max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="f500-card p-6">
          <div className="flex items-center gap-3 text-[var(--f500-ink-2)]" style={{ fontFamily: 'var(--sf-text)' }}>
            <Loader2 className="w-5 h-5 animate-spin" />
            Loading admin dashboard…
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="f500-page max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="border-l-4 border-[var(--f500-red)] bg-[#FFF5F4] p-6 text-[#7A1813]">
          <div className="flex items-center gap-2 font-semibold f500-h3"><ShieldAlert className="w-5 h-5" /> Admin access required</div>
          <p className="mt-2 text-sm" style={{ fontFamily: 'var(--sf-text)' }}>{error}</p>
        </div>
      </div>
    );
  }

  const renderFilterPanel = () => {
    if (tab === 'revenue') {
      return (
        <div className="mt-6 f500-card p-6">
          <RevenueFilters
            revStart={revStart} setRevStart={setRevStart}
            revEnd={revEnd} setRevEnd={setRevEnd}
            revStatus={revStatus} setRevStatus={setRevStatus}
            revCurrency={revCurrency} setRevCurrency={setRevCurrency}
            revEmail={revEmail} setRevEmail={setRevEmail}
            revPlan={revPlan} setRevPlan={setRevPlan}
            onApply={applyRevenueFilters}
          />
          <div className="mt-4 text-sm text-[var(--f500-ink-2)]" style={{ fontFamily: 'var(--sf-text)' }}>
            Payments: <span className="font-semibold text-[var(--f500-ink)]">{paymentsData.rows.length}</span>
            {' '}• Subscriptions: <span className="font-semibold text-[var(--f500-ink)]">{subsData.rows.length}</span>
            {' '}• Events: <span className="font-semibold text-[var(--f500-ink)]">{eventsData.rows.length}</span>
          </div>
        </div>
      );
    }
    if (tab === 'storage') {
      return (
        <div className="mt-6 f500-card p-6">
          <div className="text-center text-[var(--f500-ink-2)] py-2" style={{ fontFamily: 'var(--sf-text)' }}>
            <p className="text-xs">Storage analytics load automatically. Click <span className="text-[var(--f500-ink)] font-semibold">Refresh</span> to update.</p>
          </div>
          <div className="mt-4 text-sm text-[var(--f500-ink-2)]" style={{ fontFamily: 'var(--sf-text)' }}>
            Platform storage overview {storageLoading && '(loading…)'}
          </div>
        </div>
      );
    }
    if (tab === 'audit') {
      return (
        <div className="mt-6 f500-card p-6">
          <AuditFilters
            auditAction={auditAction} setAuditAction={setAuditAction}
            auditEmail={auditEmail} setAuditEmail={setAuditEmail}
            onApply={loadAudit}
          />
          <div className="mt-4 text-sm text-[var(--f500-ink-2)]" style={{ fontFamily: 'var(--sf-text)' }}>
            Audit entries: <span className="font-semibold text-[var(--f500-ink)]">{auditRows.length}</span> {auditLoading && '(loading…)'}
          </div>
        </div>
      );
    }
    return null;
  };

  const renderTabContent = () => {
    if (tab === 'health') return <HealthTab />;
    if (tab === 'llm_health') return <LlmHealthTab />;
    if (tab === 'storage_health') return <StorageHealthTab />;
    if (tab === 'users') return <UsersTab />;
    if (tab === 'ambassadors') return <AmbassadorsTab rows={rows} />;
    if (tab === 'revenue') return (
      <RevenueTab
        paymentsLoading={paymentsLoading}
        subsLoading={subsLoading}
        eventsLoading={eventsLoading}
        paymentsData={paymentsData}
        subsData={subsData}
        eventsData={eventsData}
      />
    );
    if (tab === 'storage') return <StorageTab data={storageData} loading={storageLoading} />;
    if (tab === 'support') return (
      <AdminSupportTab
        tickets={supportTickets}
        loading={supportLoading}
        stats={supportStats}
        analytics={supportAnalytics}
        onReload={loadSupportTickets}
      />
    );
    if (tab === 'addons_revenue') return <AddonsRevenueTab data={revenueData} loading={revenueLoading} />;
    if (tab === 'content') return <ContentTab />;
    if (tab === 'pioneers') return <PioneersTab />;
    return <AuditTab rows={auditRows} loading={auditLoading} />;
  };

  return (
    <div className="f500-page max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="admin-page">
      <AdminHeader tab={tab} userEmail={user?.email} onRefresh={handleRefresh} onDownload={handleDownload} />
      <AdminTabs tab={tab} onChange={setTab} />
      {renderFilterPanel()}
      <div className="mt-6">
        {renderTabContent()}
      </div>
    </div>
  );
};

export default AdminPage;
